/**
 * Client-side cart storage for the fake cart drawer.
 * Loaded synchronously from theme.liquid when fake cart implementation is active.
 */
(function () {
  const STORAGE_KEY = "tlts_fake_cart";
  const STORAGE_VERSION = 1;

  /**
   * Presentment currency the storefront is currently rendering in. Injected from
   * theme.liquid (cart.currency), with a Shopify.currency runtime fallback.
   */
  function currentCurrency() {
    if (window.TLTS_MARKET && window.TLTS_MARKET.currency) return String(window.TLTS_MARKET.currency);
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return String(window.Shopify.currency.active);
    }
    return null;
  }

  function createEmptyCart() {
    return { v: STORAGE_VERSION, c: currentCurrency(), items: [] };
  }

  function readRaw() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return createEmptyCart();

      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return createEmptyCart();

      // Currency guard: the fake cart freezes each line's price at add-time, so a
      // cart captured in another presentment currency holds stale prices. Discard
      // it rather than compare mismatched currencies (e.g. USD cents vs GBP pence).
      const cur = currentCurrency();
      if (cur && parsed.c && parsed.c !== cur) {
        return createEmptyCart();
      }

      return {
        v: STORAGE_VERSION,
        c: parsed.c || cur,
        // Backfill handle on carts persisted before the field existed, so the
        // availability check can look them up without waiting for a re-add.
        items: parsed.items.map((item) =>
          item && !item.handle ? { ...item, handle: resolveHandle(item) } : item,
        ),
      };
    } catch {
      return createEmptyCart();
    }
  }

  function writeRaw(data) {
    const payload = {
      v: STORAGE_VERSION,
      c: currentCurrency() || (data && data.c) || null,
      items: data && Array.isArray(data.items) ? data.items : [],
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  function dispatchUpdate() {
    const detail = { cart: window.FakeCart.getState() };
    // Dispatch on both window and document so listeners on either target fire.
    window.dispatchEvent(new CustomEvent("fake-cart:updated", { detail }));
    document.dispatchEvent(new CustomEvent("fake-cart:updated", { detail }));
  }

  function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function normalizeProperties(properties) {
    if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
      return {};
    }

    const normalized = {};
    Object.keys(properties).forEach((key) => {
      if (properties[key] != null && properties[key] !== "") {
        normalized[key] = String(properties[key]);
      }
    });
    return normalized;
  }

  function propertiesKey(properties) {
    const keys = Object.keys(properties).sort();
    return keys.map((key) => `${key}:${properties[key]}`).join("|");
  }

  /**
   * Product handle for a line, used by the availability check to look up
   * /products/{handle}.js. Prefers an explicit handle (data-fake-cart-handle);
   * falls back to parsing the stored product url so carts persisted before the
   * handle attribute existed stay checkable.
   */
  function resolveHandle(input) {
    const explicit = input.handle ?? input.product_handle ?? input.productHandle;
    if (explicit) return String(explicit);

    const url = input.url ? String(input.url) : "";
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }

  function normalizeItem(input) {
    if (!input || typeof input !== "object") {
      throw new Error("FakeCart.addItem: item must be an object");
    }

    const variantId = toNumber(input.id ?? input.variant_id ?? input.variantId);
    if (!variantId) {
      throw new Error("FakeCart.addItem: variant id is required");
    }

    const properties = normalizeProperties(input.properties);
    let sellingPlanId =
      input.selling_plan_id ?? input.sellingPlanId ?? properties._selling_plan_id ?? null;

    if (sellingPlanId != null && sellingPlanId !== "") {
      sellingPlanId = String(sellingPlanId);
      properties._selling_plan_id = sellingPlanId;
    }

    const quantity = Math.max(1, parseInt(input.quantity, 10) || 1);

    return {
      id: variantId,
      product_id: toNumber(input.product_id ?? input.productId) ?? null,
      quantity,
      properties,
      selling_plan_id: sellingPlanId || null,
      title: input.title ? String(input.title) : "",
      variant_title: input.variant_title ?? input.variantTitle ?? "",
      price: toNumber(input.price) ?? 0,
      compare_at_price: toNumber(input.compare_at_price ?? input.compareAtPrice) ?? 0,
      image: input.image ? String(input.image) : "",
      url: input.url ? String(input.url) : "",
      handle: resolveHandle(input),
      key: `${variantId}:${propertiesKey(properties)}:${sellingPlanId || ""}`,
    };
  }

  function sumQuantity(items) {
    return items.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  function mergeItemMetadata(existing, incoming) {
    ["product_id", "title", "variant_title", "price", "image", "url", "handle"].forEach((field) => {
      if ((existing[field] == null || existing[field] === "" || existing[field] === 0) && incoming[field]) {
        existing[field] = incoming[field];
      }
    });

    const incomingCompare = toNumber(incoming.compare_at_price ?? incoming.compareAtPrice) ?? 0;
    const existingCompare = existing.compare_at_price || 0;
    if (incomingCompare > existingCompare) {
      existing.compare_at_price = incomingCompare;
    }
  }

  function findMatchingIndex(items, item) {
    return items.findIndex((existing) => existing.key === item.key);
  }

  function moveLineToFront(items, index) {
    if (index <= 0 || index >= items.length) return;
    const [line] = items.splice(index, 1);
    items.unshift(line);
  }

  const FakeCart = {
    /** @returns {object[]} */
    getItems() {
      return readRaw().items.slice();
    },

    /** Alias for getItems */
    getProducts() {
      return this.getItems();
    },

    /** @returns {{ items: object[], item_count: number, isEmpty: boolean }} */
    getState() {
      const items = readRaw().items;
      return {
        items: items.slice(),
        item_count: sumQuantity(items),
        isEmpty: items.length === 0,
      };
    },

    isEmpty() {
      return readRaw().items.length === 0;
    },

    getItemCount() {
      return sumQuantity(readRaw().items);
    },

    getTotalPrice() {
      return readRaw().items.reduce((total, item) => total + (item.price || 0) * (item.quantity || 0), 0);
    },

    /**
     * @param {object} input
     * @returns {object} Updated cart state
     */
    addItem(input) {
      const item = normalizeItem(input);
      const data = readRaw();
      const index = findMatchingIndex(data.items, item);

      if (index >= 0) {
        data.items[index].quantity += item.quantity;
        mergeItemMetadata(data.items[index], item);
        moveLineToFront(data.items, index);
      } else {
        data.items.unshift(item);
      }

      writeRaw(data);
      dispatchUpdate();
      return this.getState();
    },

    /** Alias for addItem */
    addProduct(input) {
      return this.addItem(input);
    },

    clear() {
      writeRaw(createEmptyCart());
      dispatchUpdate();
      return this.getState();
    },

    /**
     * Replace the entire fake cart with the given line inputs (normalized + de-duped by key).
     * Used when reconciling from the Shopify Ajax Cart API after checkout return.
     * @param {object[]} inputs
     * @returns {object} Updated cart state
     */
    replaceItems(inputs) {
      const normalized = (Array.isArray(inputs) ? inputs : [])
        .map((input) => {
          try {
            return normalizeItem(input);
          } catch {
            return null;
          }
        })
        .filter(Boolean);

      const items = [];
      normalized.forEach((item) => {
        const index = findMatchingIndex(items, item);
        if (index >= 0) {
          items[index].quantity += item.quantity;
          mergeItemMetadata(items[index], item);
        } else {
          items.push(item);
        }
      });

      writeRaw({ v: STORAGE_VERSION, items });
      dispatchUpdate();
      return this.getState();
    },

    /**
     * Set absolute quantity for a variant (PLP ey-qty). Merges duplicate variant lines.
     * @param {number|string} variantId
     * @param {number} quantity
     * @param {object} [metadata] - title, price, image, url for new lines
     */
    updateVariantQuantity(variantId, quantity, metadata) {
      const vid = String(variantId);
      const data = readRaw();
      // Ignore free-gift lines so PLP ey-qty never merges/removes conditional gifts.
      const isGift = (item) =>
        item && item.properties && String(item.properties._is_free_gift) === "true";
      const existingIndex = data.items.findIndex(
        (item) => String(item.id) === vid && !isGift(item)
      );
      const existing = existingIndex >= 0 ? data.items[existingIndex] : null;
      const others = data.items.filter((item) => String(item.id) !== vid || isGift(item));
      const qty = Math.max(0, parseInt(quantity, 10) || 0);

      if (qty === 0) {
        writeRaw({ v: STORAGE_VERSION, items: others });
        dispatchUpdate();
        return this.getState();
      }

      const nextItem = existing
        ? { ...existing, quantity: qty }
        : normalizeItem({ id: variantId, quantity: qty, ...(metadata || {}) });

      const items = existing ? data.items.slice() : [nextItem, ...others];
      if (existing) {
        items[existingIndex] = nextItem;
      }

      writeRaw({ v: STORAGE_VERSION, items });
      dispatchUpdate();
      return this.getState();
    },

    /**
     * @param {number|string} variantId
     * @param {number} [delta]
     */
    adjustVariantQuantity(variantId, delta, metadata) {
      const vid = String(variantId);
      const existing = readRaw().items.find((item) => String(item.id) === vid);
      const current = existing ? existing.quantity : 0;
      return this.updateVariantQuantity(variantId, current + delta, metadata || existing || {});
    },

    changeLineQuantity(key, delta) {
      const item = readRaw().items.find((line) => line.key === key);
      if (!item) return this.getState();
      return this.setLineQuantity(key, item.quantity + delta);
    },

    removeLine(key) {
      const data = readRaw();
      data.items = data.items.filter((item) => item.key !== key);
      writeRaw(data);
      dispatchUpdate();
      return this.getState();
    },

    /**
     * Remove several lines at once (availability check dropping unbuyable
     * products, or checkout recovering from a rejected line).
     * @param {string[]} keys
     * @returns {object} Updated cart state
     */
    removeLines(keys) {
      const doomed = new Set((Array.isArray(keys) ? keys : []).map(String));
      if (doomed.size === 0) return this.getState();

      const data = readRaw();
      data.items = data.items.filter((item) => !doomed.has(item.key));
      writeRaw(data);
      dispatchUpdate();
      return this.getState();
    },

    setLineQuantity(key, quantity) {
      const qty = parseInt(quantity, 10);
      const data = readRaw();
      const index = data.items.findIndex((item) => item.key === key);
      if (index < 0) return this.getState();

      if (!Number.isFinite(qty) || qty < 1) {
        return this.getState();
      }

      data.items[index].quantity = qty;

      writeRaw(data);
      dispatchUpdate();
      return this.getState();
    },
  };

  // Currency reconciliation on load: if a persisted cart was captured in a
  // different presentment currency (buyer switched markets, or a returning
  // session got geo-routed elsewhere), clear it so stale prices never render.
  (function reconcileCurrencyOnLoad() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const cur = currentCurrency();
      if (parsed && cur && parsed.c && parsed.c !== cur) {
        writeRaw(createEmptyCart());
      }
    } catch {}
  })();

  window.FakeCart = FakeCart;
  FakeCart._initial = FakeCart.getState();
})();
